import { Chip } from "@heroui/react";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { useTranslation } from "react-i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type Audit = {
  action: "create" | "update" | "delete";
  created_at: Date;
  user_email: string;
  audited_changes: Record<string, any>;
};

type AuditChangesValue = string | number | boolean | null;
type AuditChangesDiffValue = [AuditChangesValue, AuditChangesValue];

interface DependencyIds {
  [table: string]: number[];
}

interface DeleteAuditChanges {
  from?: DependencyIds;
  to?: DependencyIds;
}

interface StandardAuditChanges {
  [field: string]: AuditChangesDiffValue;
}

type AuditChangesType = DeleteAuditChanges | StandardAuditChanges;

const AuditChangesCreation: React.FC<{ changes: Record<string, any> }> = ({
  changes,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      {Object.entries(changes)
        .filter(
          ([, value]) =>
            ![null, undefined, "null", "undefined", ""].includes(value),
        )
        .map(([field, value]) => (
          <div
            key={field}
            className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 p-0.5"
          >
            <div className="w-full sm:w-32">
              {t(`audit.fields.${field}`, { defaultValue: field })}
            </div>
            <div className="flex-1 flex items-center">
              <span className="text-green-600 font-mono ml-2">
                + {JSON.stringify(value)}
              </span>
            </div>
          </div>
        ))}
    </div>
  );
};

const ClickableIdsList: React.FC<{ table: string; ids: number[] }> = ({
  table,
  ids,
}) => (
  <>
    {ids.map((id, index) => (
      <ClickableId
        key={`id-${id}`}
        table={table}
        id={id}
        isFirst={index === 0}
      />
    ))}
  </>
);

const ClickableId: React.FC<{
  table: string;
  id: number;
  isFirst: boolean;
}> = ({ table, id, isFirst }) => (
  <>
    {!isFirst && ", "}
    <a
      href={toWorkspacePath(`/admin/${table.toLowerCase()}/${id}`)}
      target="_blank"
      rel="noopener noreferrer"
      className="text-red-500 hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {id}
    </a>
  </>
);

const AuditChangesDiff: React.FC<{ changes: AuditChangesType }> = ({
  changes,
}) => {
  const { t } = useTranslation();

  if ("from" in changes || "to" in changes) {
    const deleteChanges = changes as DeleteAuditChanges;

    return (
      <div className="space-y-4">
        {deleteChanges.to && (
          <div className="space-y-2">
            <div className="text-red-600">{t("audit.cascade.deleted")}</div>
            {Object.entries(deleteChanges.to).map(([table, ids]) => (
              <div key={`to-${table}`} className="ml-4 p-1 rounded-md">
                <div className="flex items-center">
                  <span className="font-medium">
                    {t(`audit.tables.${table}`, { defaultValue: table })}:
                  </span>
                  <span className="ml-2">
                    <ClickableIdsList table={table} ids={ids} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {deleteChanges.from && (
          <div className="space-y-2">
            <div className="text-red-600">
              {t("audit.cascade.deletedBecauseOf")}
            </div>
            {Object.entries(deleteChanges.from).map(([table, ids]) => (
              <div key={`from-${table}`} className="ml-4 p-1 rounded-md">
                <div className="flex items-center">
                  <span className="font-medium">
                    {t(`audit.tables.${table}`, { defaultValue: table })}:
                  </span>
                  <span className="ml-2">
                    <ClickableIdsList table={table} ids={ids} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const standardChanges = changes as StandardAuditChanges;

  return (
    <div className="space-y-2">
      {Object.entries(standardChanges).map(([field, values]) => {
        const [before, after] = values;
        return (
          <div
            key={field}
            className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 p-0.5 rounded-md"
          >
            <div className="w-full sm:w-32">
              {t(`audit.fields.${field}`, { defaultValue: field })}
            </div>
            <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-left">
              <span className="text-red-600 font-mono mx-2">
                - {before !== null ? String(before) : t("audit.empty")}
              </span>
              <span className="hidden sm:inline-block text-gray-500">→</span>
              <span className="text-green-600 font-mono ml-2">
                + {after !== null ? String(after) : t("audit.empty")}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const formatAuditChanges = (
  action: "create" | "update" | "delete",
  auditedChanges: Record<string, any>,
) =>
  action === "create" ? (
    <AuditChangesCreation changes={auditedChanges} />
  ) : (
    <AuditChangesDiff changes={auditedChanges as AuditChangesType} />
  );

export function CombinedInfoCell({ row }: { row: { original: Audit } }) {
  const { t, i18n } = useTranslation();
  const { action, created_at, user_email } = row.original;

  const dateObj = new Date(created_at + "Z");
  const date = dateObj.toLocaleString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const chipColor =
    action === "create"
      ? "success"
      : action === "update"
        ? "warning"
        : action === "delete"
          ? "danger"
          : "default";

  return (
    <div className="flex items-center gap-4">
      <Chip color={chipColor} variant="bordered">
        {t(`audit.actions.${action}`, { defaultValue: action })}
      </Chip>

      <div className="flex flex-col">
        <span className="font-medium">{user_email}</span>
        <span className="text-gray-500 mt-1">{date}</span>
      </div>
    </div>
  );
}

export const columns: ColumnDef<Audit>[] = [
  {
    accessorKey: "combinedInfo",
    header: () => {
      const { t } = useTranslation();
      return t("audit.columns.info");
    },
    cell: ({ row }) => <CombinedInfoCell row={row} />,
  },
  {
    accessorKey: "audited_changes",
    header: () => {
      const { t } = useTranslation();
      return t("audit.columns.changes");
    },
    cell: ({ row }) => {
      const { action, audited_changes } = row.original;
      return formatAuditChanges(action, audited_changes);
    },
  },
];
